<script lang="ts" setup>
  import { computed, type ComputedRef, inject } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { BaseButton } from '@qic/ui-components'
  import CoinsBadge from '@qicgroupdoha/loyalty-components/components/CoinsBadge'
  import {
    LEARN_MORE_POPUP_EVENT,
    SELECT_PLAN_EVENT_NAME,
    TOGGLE_EVENT_NAME,
  } from './FlowPlan.constants'
  import { TravelDirection } from '@/shared/types/common.types'
  import FlowPlanCoverage from '@/entities/Flow/components/FlowPlanCoverage/FlowPlanCoverage.vue'
  import type {
    Emits,
    Props,
    Slots,
  } from '@/entities/Flow/components/FlowPlan/FlowPlanInsuranceValues.types'
  import BaseChip from '@/shared/components/BaseChip/BaseChip.vue'
  import TicketChancesBlock from '@/shared/components/TicketChancesBlock/TicketChancesBlock.vue'
  import { PLAN_NAMES_SCHEME_TYPE_CODE_MAP } from '@/shared/constants/plans.constants'
  import { toFixedNumber } from '@/shared/utils'
  import { useDiscount } from '@/shared/composables'
  import { useQuoteStore } from '@/shared/store'
  import CoinsDiscountInfo from './components/CoinsDiscountInfo/CoinsDiscountInfo.vue'

  const props = withDefaults(defineProps<Props>(), {
    variant: 'worldwide',
    isRecomended: false,
    typeOfTravel: TravelDirection.UNKNOWN,
    travelersCount: 1,
  })

  const emit = defineEmits<Emits>()
  defineSlots<Slots>()

  const { t } = useI18n()
  const quoteStore = useQuoteStore()

  const discountScheme = computed(() => ({
    discounts: props.plan.discounts,
    price: props.plan.price,
  }))
  const { discount } = useDiscount(discountScheme)
  const quotePriceWithoutDiscount = computed(() =>
    toFixedNumber(props.plan?.price + discount.value.amount)
  )

  const planTitle = computed(() =>
    t(PLAN_NAMES_SCHEME_TYPE_CODE_MAP[props.plan.schemeTypeCode] ?? '')
  )
  const isRamadanCampaign = inject('isRamadanCampaign') as ComputedRef<boolean>

  const handleSelectPlan = () => {
    emit(SELECT_PLAN_EVENT_NAME)
  }

  const handleToggle = (isOpen: boolean, rowId: string, rowName: string) => {
    emit(TOGGLE_EVENT_NAME, isOpen, rowId, rowName)
  }
</script>

<template>
  <div :class="['flow-plan', { 'flow-plan--recommended': props.isRecomended }]">
    <div :class="['flow-plan__header', { 'flow-plan__header--recommended': props.isRecomended }]">
      <div class="flow-plan__title">{{ planTitle }}</div>
      <div
        :class="[
          'flow-plan__description',
          { 'flow-plan__description--recommended': props.isRecomended },
        ]"
      >
        <slot name="plan-description"></slot>
      </div>
      <CoinsBadge
        v-if="quoteStore.productBonus > 0 && !plan.priceWithCoinsDiscount"
        :text="t('select_plan.coins', { value: quoteStore.productBonus })"
        class="flow-plan__coins-badge"
      />
    </div>
    <div class="flow-plan__content">
      <FlowPlanCoverage
        :plan="plan"
        :with-price="props.variant === 'single'"
        :type-of-travel="typeOfTravel"
        @toggle="handleToggle"
      />
      <button
        class="flow-plan__learn-more"
        @click="emit(LEARN_MORE_POPUP_EVENT, plan.schemeTypeCode)"
      >
        {{ $t('qic.common.learn_more') }}
      </button>
      <div class="flow-plan__price-info">
        <div class="flow-plan__price-wrapper">
          <span class="flow-plan__price ltr">
            {{ `${$t('qar')} ${toFixedNumber(props.plan.price)}` }}
          </span>
          <span v-if="discount.amount > 0" class="flow-plan__price flow-plan__price--discount ltr">
            {{ `${$t('qar')} ${toFixedNumber(quotePriceWithoutDiscount)}` }}
          </span>
        </div>
        <CoinsDiscountInfo
          v-if="plan.priceWithCoinsDiscount"
          :discount-price="Number(plan.priceWithCoinsDiscount)"
        />
        <span class="flow-plan__price-description">
          {{
            $t(
              'outbound.select_plan.insurance_values.plan_price_annotation',
              { travellerCount: props.travelersCount },
              props.travelersCount
            )
          }}
        </span>
      </div>
      <BaseButton
        v-if="!isSinglePlan"
        class="flow-plan__footer-button"
        medium
        icon="none"
        @click="handleSelectPlan"
      >
        {{ selectPlanButtonTxt }}
      </BaseButton>
      <TicketChancesBlock v-if="isRamadanCampaign" class="flow-plan__ticket" />
    </div>
    <div class="flow-plan__badges">
      <BaseChip
        v-if="discount.percentage"
        variant="purple"
        class="flow-plan__badge flow-plan__badge-discount"
      >
        {{ discount.name }}&nbsp;<span class="ltr">{{ `−${discount.percentage}%` }}</span>
      </BaseChip>
      <BaseChip v-if="badgeText" variant="green" class="flow-plan__badge">{{ badgeText }}</BaseChip>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import './FlowPlanInsuranceValues';

  .outbound .chip_purple :deep(.text) {
    color: #494bb5;
  }

  .outbound .flow-plan__badge :deep(.text) {
    @include caption-6-plus;
  }
</style>
